import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import type { PcoGroupDetail, PcoEventDetail, PcoMessageDetail } from './types';

const formatDate = (dateStr: string, opts?: Intl.DateTimeFormatOptions) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', opts || {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
};

const formatTime = (dateStr: string) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit'
  });
};

const GroupDetail: React.FC<{ data: PcoGroupDetail }> = ({ data }) => (
  <div className="pco-detail-content">
    {data.image_url && (
      <img className="pco-detail-hero" src={data.image_url} alt={data.name} />
    )}
    <h1>{data.name}</h1>
    <div className="pco-detail-meta">
      {data.group_type && <span>{data.group_type}</span>}
      {data.schedule && <span>📅 {data.schedule}</span>}
      {data.location && <span>📍 {data.location}</span>}
    </div>
    {data.description && (
      <div className="pco-detail-body" dangerouslySetInnerHTML={{ __html: data.description }} />
    )}
    {data.enrollment_url && (
      <a className="pco-detail-btn" href={data.enrollment_url} target="_blank" rel="noopener">
        Join This Group
      </a>
    )}
  </div>
);

const EventDetail: React.FC<{ data: PcoEventDetail }> = ({ data }) => (
  <div className="pco-detail-content">
    {data.image_url && (
      <img className="pco-detail-hero" src={data.image_url} alt={data.name} />
    )}
    <h1>{data.name}</h1>
    <div className="pco-detail-meta">
      {data.starts_at && <span>📅 {formatDate(data.starts_at)}</span>}
      {data.starts_at && (
        <span>🕐 {formatTime(data.starts_at)}{data.ends_at ? ` – ${formatTime(data.ends_at)}` : ''}</span>
      )}
      {data.location && <span>📍 {data.location}</span>}
    </div>
    {data.description && (
      <div className="pco-detail-body" dangerouslySetInnerHTML={{ __html: data.description }} />
    )}
    {data.registration_url && (
      <a className="pco-detail-btn" href={data.registration_url} target="_blank" rel="noopener">
        Register
      </a>
    )}
  </div>
);

const MessageDetail: React.FC<{ data: PcoMessageDetail }> = ({ data }) => (
  <div className="pco-detail-content">
    {data.image_url && (
      <img className="pco-detail-hero" src={data.image_url} alt={data.title} />
    )}
    <h1>{data.title}</h1>
    {data.series_title && <div className="pco-detail-series">{data.series_title}</div>}
    <div className="pco-detail-meta">
      {data.speaker && <span>🎤 {data.speaker}</span>}
      {data.published_at && <span>📅 {formatDate(data.published_at)}</span>}
    </div>
    {data.video_url && (
      <div className="pco-detail-player">
        <video controls preload="metadata" src={data.video_url} />
      </div>
    )}
    {!data.video_url && data.audio_url && (
      <div className="pco-detail-player">
        <audio controls preload="metadata" src={data.audio_url} />
      </div>
    )}
    {data.description && (
      <div className="pco-detail-body" dangerouslySetInnerHTML={{ __html: data.description }} />
    )}
  </div>
);

type DetailData = PcoGroupDetail | PcoEventDetail | PcoMessageDetail;

const DETAIL_COMPONENTS: Record<string, React.ComponentType<{ data: any }>> = {
  group: GroupDetail,
  event: EventDetail,
  message: MessageDetail,
};

interface DetailModalProps {
  itemType: string;
  itemId: string;
  siteUniqueName: string;
  host: string;
  protocol: string;
  onClose: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ itemType, itemId, siteUniqueName, host, protocol, onClose }) => {
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    const p = protocol || 'https';
    const h = host;

    API.getPcoDetail(p, h, siteUniqueName, itemType, itemId)
      .then((json: any) => {
        if (json && json.error) {
          // Server returned a classified error (connection_expired, rate_limited, etc.).
          // The modal is user-initiated on published sites — show a subtle, non-PCO-branded message.
          setError(json.error === 'not_found' ? 'Not found.' : "Couldn't load details right now.");
        } else if (json && json.data) {
          setData(json.data);
        } else {
          setError('Not found.');
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Couldn't load details right now.");
        setLoading(false);
      });
  }, [protocol, host, siteUniqueName, itemType, itemId]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const DetailComponent = DETAIL_COMPONENTS[itemType];

  return (
    <div className="pco-modal-backdrop" onClick={handleBackdropClick}>
      <div className="pco-modal">
        <button className="pco-modal-close" onClick={onClose} aria-label="Close">✕</button>
        {loading && (
          <div className="pco-modal-loading"><p>Loading...</p></div>
        )}
        {error && (
          <div className="pco-modal-loading"><p>{error}</p></div>
        )}
        {!loading && !error && data && DetailComponent && (
          <DetailComponent data={data} />
        )}
      </div>
    </div>
  );
};

export default DetailModal;
